/**
 * The system administrator's pages (§3.3): the overview, every account, every
 * organization, and one of each in detail.
 *
 * Accounts and organizations are two lists rather than one, because they answer
 * two questions — "who is there" and "which tenants are there" — and most of the
 * customer base is a personal account that appears in no organization at all.
 * Either list reaches the other: an account names the organizations it belongs
 * to, and an organization names the addresses on its seats.
 *
 * Every figure here is computed by the Worker from the tables — nothing on this
 * page is a fixture, and where a number is an approximation it says so rather
 * than being drawn as though it were exact.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { Card, Empty, GhostButton, Input, Row, RowAction, StatStrip, StatusCell, Table } from '../components/primitives';
import { useConfirm } from '../components/Modal';
import { api, ApiError, apiPath, apiQuery } from '../lib/api';
import { isoDay, proseDay } from '../lib/format';
import { ACCESS_LABEL, money } from '../lib/format';
import type { AdminAccount, AdminAccountDetail as AccountDetail, AdminOrg, Overview, OrgDetail } from '../lib/types';
import { Administrators } from './AdminAdmins';
import { AffiliateAlert } from './AdminAffiliates';
import { OrgBilling } from './AdminOrgBilling';
import { OrgCloud } from './AdminOrgCloud';
import { SerialLine, useSerialActions } from './AdminSerials';
import { BetaGrant, BetaSeats, DaysSentence, daysValid, FIX_DAYS, HeldGhost } from './AdminBeta';
import { DateCell } from '../components/DateCell';

/** The dot beside a licence state, in the same four colours the rest uses. */
const ACCESS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'off'> = {
  active: 'ok',
  grace: 'warn',
  lapsed: 'off',
  revoked: 'bad',
  none: 'off'
};

export function AdminOverview({ onAffiliates }: { onAffiliates: () => void }): React.JSX.Element {
  const [data, setData] = useState<Overview | null>(null);

  useEffect(() => {
    void api
      .get<Overview>('/admin/overview')
      .then(setData)
      .catch(() => setData(null));
  }, []);

  if (!data) return <Empty title="Counting…" />;

  const delta = data.signups.last30 - data.signups.previous30;
  const months = data.signups.monthly;
  const peak = Math.max(1, ...months.map((month) => month.count));
  const total = data.licences.active + data.licences.grace + data.licences.lapsed + data.licences.revoked;
  const share = (count: number): string => `${total === 0 ? 0 : Math.round((count / total) * 100)}%`;
  const signups = months.reduce((sum, month) => sum + month.count, 0);
  // « Nov », and « Oct 2026 » in a bar's tooltip (§47.2): a month said as a word.
  const monthWord = (month: string, year = false): string =>
    new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' });
  const signupsIn = (month: { month: string; count: number }): string =>
    `${monthWord(month.month, true)} · ${month.count} signup${month.count === 1 ? '' : 's'}`;
  // The split in status colours (§47.2); a segment at 0 is not drawn but stays in the legend.
  const split = [
    { name: 'Active', count: data.licences.active, tint: color.accent },
    { name: 'In grace', count: data.licences.grace, tint: '#C9A03A' },
    { name: 'Lapsed', count: data.licences.lapsed, tint: color.lineInput },
    { name: 'Revoked', count: data.licences.revoked, tint: color.red }
  ];

  return (
    <>
      <AffiliateAlert onOpen={onAffiliates} />
      <StatStrip
        columns={4}
        valueSize={28}
        items={[
          { label: 'MRR', value: `$${data.mrrUsd.toLocaleString('en-GB')}`, delta: 'active subscriptions', deltaTone: 'ok' },
          { label: 'ARR', value: `$${data.arrUsd.toLocaleString('en-GB')}`, delta: 'annual run rate' },
          {
            label: 'Active licences',
            value: String(data.licences.active),
            delta: `${data.licences.grace} in grace · ${data.licences.lapsed} lapsed`
          },
          {
            label: 'Organizations',
            value: String(data.organizations.count),
            delta: `${data.organizations.seats} seats reserved`
          },
          {
            label: 'Signups (30d)',
            value: String(data.signups.last30),
            delta: `${delta >= 0 ? '+' : ''}${delta} vs previous 30d`,
            deltaTone: delta >= 0 ? 'ok' : 'muted'
          },
          { label: 'Lapse rate', value: `${data.churnPct}%`, delta: 'lapsed over the paying base' },
          { label: 'Revocations (90d)', value: String(data.revocations90), delta: 'refunds and key sharing' },
          { label: 'Revoked in total', value: String(data.licences.revoked), delta: 'still counted, never deleted' }
        ]}
      />

      <div className="al-two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: 20, marginTop: 20 }}>
        <Card padding={24}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Signups</div>
            <span style={{ fontSize: 12.5, color: color.inkFaint }}>
              {signups} signup{signups === 1 ? '' : 's'} in {months.length} month{months.length === 1 ? '' : 's'}
            </span>
          </div>
          {months.length === 0 ? (
            <p style={{ fontSize: 13.5, color: color.inkFaint, marginTop: 18 }}>No accounts yet.</p>
          ) : (
            // `justifyContent: flex-start` and a capped column width, so a chart
            // with two months of history is still a chart rather than one bar
            // the width of the card. Each bar is focusable and says its month
            // and count (§47.2); the current month is the full teal.
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', gap: 8, height: 170, marginTop: 22 }}>
              {months.map((month, index) => (
                <div
                  key={month.month}
                  style={{ flex: 1, maxWidth: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
                >
                  <div
                    tabIndex={0}
                    title={signupsIn(month)}
                    aria-label={signupsIn(month)}
                    style={{
                      width: '100%',
                      height: Math.max(2, Math.round((month.count / peak) * 140)),
                      background: color.accent,
                      opacity: index === months.length - 1 ? 1 : 0.55,
                      borderRadius: '4px 4px 0 0',
                      transition: 'opacity 150ms ease'
                    }}
                  />
                  <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkSubtle }}>{monthWord(month.month)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card padding={24}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Licences</div>
          <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', marginTop: 20, gap: 2 }}>
            {split
              .filter((part) => part.count > 0)
              .map((part) => (
                <div key={part.name} style={{ width: share(part.count), background: part.tint }} />
              ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16, fontSize: 13.5 }}>
            {split.map((part) => (
              <div key={part.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontVariantNumeric: 'tabular-nums' }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: part.tint, flex: 'none' }} />
                <span style={{ flex: 1 }}>{part.name}</span>
                <strong style={{ fontWeight: 600 }}>{part.count}</strong>
                <span style={{ width: 40, textAlign: 'right', fontFamily: font.mono, fontSize: 12, color: color.inkSubtle }}>
                  {share(part.count)}
                </span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12.5, color: color.inkFaint, margin: '18px 0 0', lineHeight: 1.6 }}>
            A licence in grace still works: the paid period is over and the week of slack is not. Lapsed means the gate is
            already refusing it.
          </p>
        </Card>
      </div>

      <Card padding={24} style={{ marginTop: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>Recent events</div>
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: 14 }}>
          {data.events.length === 0 && <p style={{ fontSize: 13.5, color: color.inkFaint }}>Nothing recorded yet.</p>}
          {data.events.map((event, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                gap: 12,
                padding: '11px 0',
                borderBottom: `1px solid ${color.surfaceAlt}`,
                fontSize: 13.5,
                alignItems: 'baseline'
              }}
            >
              <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkGhost, width: 130, flex: 'none' }}>
                {event.at.replace('T', ' ').slice(0, 16)}
              </span>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background:
                    event.level === 'ok'
                      ? color.accent
                      : event.level === 'warn'
                        ? color.yellow
                        : event.level === 'bad'
                          ? color.red
                          : color.blue,
                  display: 'block',
                  flex: 'none'
                }}
              />
              <span style={{ flex: 1 }}>{event.text}</span>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

export function AdminOrgs({ onInspect }: { onInspect: (id: string) => void }): React.JSX.Element {
  const [orgs, setOrgs] = useState<AdminOrg[] | null>(null);
  const [filter, setFilter] = useState('');
  const columns = '1.3fr 110px 130px 120px 120px 110px';

  useEffect(() => {
    void api
      .get<{ organizations: AdminOrg[] }>('/admin/orgs')
      .then((answer) => setOrgs(answer.organizations))
      .catch(() => setOrgs([]));
  }, []);

  if (orgs === null) return <Empty title="Reading the tenants…" />;
  if (orgs.length === 0) return <Empty title="No organizations yet">One appears here as soon as somebody creates it.</Empty>;

  const query = filter.trim().toLowerCase();
  const visible = query
    ? orgs.filter((org) => org.name.toLowerCase().includes(query) || org.slug.includes(query))
    : orgs;
  const seats = orgs.reduce((total, org) => total + org.seats, 0);

  return (
    <Table
      columns={columns}
      header={['Organization', 'Seats assigned', 'Active in 30 days', 'MRR', 'Status', '']}
      toolbar={
        <>
          <label htmlFor="org-filter" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            Filter organizations
          </label>
          <Input
            id="org-filter"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter organizations…"
            style={{ width: 'auto', minWidth: 260, fontSize: 13.5, padding: '9px 12px', borderRadius: radius.md }}
          />
          <span style={{ flex: 1 }} />
          <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>
            {orgs.length} organizations · {seats} seats
          </span>
        </>
      }
    >
      {visible.map((org) => {
        // Active members over seats, capped: members active without a seat made
        // this read 110 % before the redesign named the two measures (proposal 34).
        // Both measures are over the seats bought on Billing (§47.6); an
        // organization on a grant has bought none, and its seats are the grant's.
        const bought = org.seatsPaid > 0 ? org.seatsPaid : org.seats;
        const pct = bought === 0 ? 0 : Math.min(100, Math.round((org.active30 / bought) * 100));
        return (
          <Row key={org.id} columns={columns}>
            <div>
              <div style={{ fontWeight: 500 }}>{org.name}</div>
              <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                {org.slug} · {org.country}
              </div>
            </div>
            {/* The two seat measures (§47.6), both over the seats bought:
                assigned, the denominator in grey, and the active share as a
                gauge capped at 100 % that fills on its first showing. */}
            <span style={{ fontFamily: font.mono, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
              {org.assigned}
              <span style={{ color: color.inkSubtle }}> / {bought}</span>
            </span>
            <div>
              <div style={{ height: 7, borderRadius: 4, background: color.surfaceAlt, overflow: 'hidden' }}>
                <div className="al-gauge" style={{ width: `${pct}%`, height: 7, borderRadius: 4, background: color.accent }} />
              </div>
              <div style={{ fontSize: 11.5, color: color.inkFaint, marginTop: 5 }}>
                {org.active30 === 0 ? 'no activity' : `${Math.min(org.active30, bought)} of ${bought} active in 30 days`}
              </div>
            </div>
            <span>${org.mrrUsd}</span>
            <StatusCell tone={org.status === 'active' ? 'ok' : org.status === 'grace' ? 'warn' : org.status === 'revoked' ? 'bad' : 'off'} word={org.status} />
            <RowAction onClick={() => onInspect(org.id)}>Inspect →</RowAction>
          </Row>
        );
      })}
    </Table>
  );
}

export function AdminOrgDetail({ id, onBack }: { id: string; onBack: () => void }): React.JSX.Element {
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [dialog, ask] = useConfirm();
  // One field for the whole list rather than one per row: the question is how
  // long this pilot runs, and it has one answer for everybody in it.
  const [betaDays, setBetaDays] = useState('90');
  // Grant 7 days and Revoke, said on their own serial line (§47.5).
  const serials = useSerialActions(async () => setDetail(await api.get<OrgDetail>(apiPath`/admin/orgs/${id}`)));
  const columns = '1fr 120px 150px 130px 110px';

  useEffect(() => {
    void api
      .get<OrgDetail>(apiPath`/admin/orgs/${id}`)
      .then(setDetail)
      .catch(() => setDetail(null));
  }, [id]);

  if (!detail) return <Empty title="Reading this organization…" />;

  /**
   * A window on **one** seat. Nothing about the organization moves — which is
   * the whole of why this verb exists beside the one above it: a team of thirty
   * piloting with four should not be handed twenty-six keys.
   */
  const giveBetaSeat = async (seat: OrgDetail['seatRows'][number]): Promise<void> => {
    const asked = Number(betaDays);
    if (!daysValid(betaDays)) return;
    if (!seat.userId) return;
    const agreed = await ask({
      title: `Give ${seat.email} ${asked} days of Airlock?`,
      lede: `In ${detail.name}, with no payment behind it. They are given a seat to hold it, free and outside anything the organization has bought, and everybody else here stays as they are.`,
      confirm: `Grant ${asked} days`
    });
    if (!agreed) return;
    try {
      const answer = await api.post<{ message: string }>('/admin/actions', {
        action: 'beta-seat',
        orgId: detail.id,
        userId: seat.userId,
        days: asked
      });
      setNote(answer.message);
      setDetail(await api.get<OrgDetail>(apiPath`/admin/orgs/${id}`));
    } catch (failure) {
      setNote(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  // What a seat's own licence is worth is only worth offering where the
  // organization is not paying: a renewal would write its dates over every one.
  const paying = detail.seatsPaid > 0;
  const bought = paying ? detail.seatsPaid : detail.seats;

  return (
    <>
      {dialog}
      {serials.dialog}
      <StatStrip
        valueSize={25}
        items={[
          {
            label: 'Seats assigned',
            value: `${detail.assigned} / ${bought}`,
            delta: bought > detail.assigned ? `${bought - detail.assigned} free to give` : undefined
          },
          { label: 'MRR', value: `$${detail.mrrUsd}` },
          {
            label: 'Active in 30 days',
            value: String(detail.active30),
            delta: bought ? `${Math.min(100, Math.round((detail.active30 / bought) * 100))}% of the seats` : undefined
          },
          // Nothing was paid where the seats are a beta, and the furthest date
          // any seat here runs to is what this has always been.
          { label: paying ? 'Paid through' : 'Seats run to', value: detail.paidThrough ? proseDay(detail.paidThrough) : '—' }
        ]}
      />

      <div style={{ marginTop: 20 }}>
        <Table
          columns={columns}
          header={['Member', 'Role', 'Serial', 'Last active', 'Seat']}
          toolbar={
            <>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {detail.name}{' '}
                <span style={{ fontFamily: font.mono, fontSize: 12, fontWeight: 400, color: color.inkFaint }}>
                  {detail.slug} · {detail.country} · created {isoDay(detail.createdAt)}
                </span>
              </div>
              <span style={{ flex: 1 }} />
              <GhostButton size="sm" onClick={onBack}>
                ← All organizations
              </GhostButton>
            </>
          }
        >
          {detail.seatRows.map((seat) => (
            <Row key={seat.email} columns={columns}>
              <span>{seat.email}</span>
              <span style={{ color: color.inkSubtle, textTransform: 'capitalize' }}>{seat.role}</span>
              <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }}>
                {seat.serial ? `#${seat.serial}` : '—'}
                {seat.plan === 'beta' && seat.paidThrough ? (
                  <span style={{ display: 'block', color: color.inkFaint, fontSize: 11.5, marginTop: 3 }}>
                    beta · {seat.paidThrough}
                  </span>
                ) : null}
              </span>
              <DateCell at={seat.lastActiveAt} style={{ fontFamily: font.mono, fontSize: 12.5 }} />
              <StatusCell tone={seat.status === 'assigned' ? 'ok' : 'off'} ring={seat.status !== 'assigned'} word={seat.status} />
            </Row>
          ))}
        </Table>
      </div>

      <OrgBilling detail={detail} onChanged={async () => setDetail(await api.get<OrgDetail>(apiPath`/admin/orgs/${id}`))} />

      <OrgCloud detail={detail} />

      <BetaGrant
        kind="organization"
        id={detail.id}
        subject={detail.name}
        refuse={
          detail.deactivatedAt
            ? 'This organization is deactivated. Nothing it holds grants anything until that is undone.'
            : detail.seatsPaid > 0
              ? 'This organization pays for its seats. Its dates are Stripe’s to state, and one written here would be overwritten by the next invoice — extend a serial with Grant 7 days instead.'
              : undefined
        }
        onDone={async () => setDetail(await api.get<OrgDetail>(apiPath`/admin/orgs/${id}`))}
      />

      <Card padding={24} style={{ marginTop: 20, maxWidth: 640 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>Administrative actions</div>
        <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '10px 0 0', lineHeight: 1.6 }}>
          {paying
            ? 'This organization pays for its seats, so a window written on one of them would be overwritten by the next invoice. Extend a serial with Grant 7 days instead.'
            : 'A beta can be given to one member at a time. It writes a window on their own licence and on nothing else — their app runs and this organization’s workspaces open for them, while everybody else here stays as they are. They are given a seat to hold it, free and outside anything the organization has bought.'}
        </p>
        {!paying && (
          <DaysSentence
            id={`beta-seat-days-${detail.id}`}
            before="A beta seat runs for"
            after="and extends where one is already running."
            days={betaDays}
            onDays={setBetaDays}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
          {detail.seatRows
            // Everybody who is actually here, not only the ones already holding
            // a key or a seat: somebody with neither is exactly who a pilot is
            // given to, and a beta grant hands them the seat along with it.
            // An invitation nobody accepted is the one row with nobody in it.
            .filter(
              (seat) =>
                seat.serial !== null || (!paying && (seat.status === 'assigned' || seat.status === 'member'))
            )
            .map((seat) => {
              const canBeta = !paying && seat.userId !== null && (seat.status === 'assigned' || seat.status === 'member');
              return (
                <SerialLine
                  key={seat.email}
                  serial={seat.serial}
                  actions={serials}
                  who={
                    <>
                      {seat.email}
                      <span style={{ color: color.inkFaint }}> · {seat.role}</span>
                    </>
                  }
                  detail={
                    seat.plan === 'beta' && seat.paidThrough ? (
                      <span style={{ display: 'block', fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                        beta, {seat.access === 'active' ? 'to' : seat.access === 'grace' ? 'grace since' : 'ended'}{' '}
                        {proseDay(seat.paidThrough)}
                      </span>
                    ) : null
                  }
                  beta={
                    canBeta ? (
                      <HeldGhost reason={daysValid(betaDays) ? undefined : FIX_DAYS} onClick={() => void giveBetaSeat(seat)}>
                        {seat.plan === 'beta' ? `+${betaDays} days` : `Beta ${betaDays} days`}
                      </HeldGhost>
                    ) : undefined
                  }
                />
              );
            })}
          {detail.seatRows.every((seat) => seat.serial === null) && paying && (
            <p style={{ fontSize: 13.5, color: color.inkFaint }}>Nobody here holds a licence yet.</p>
          )}
          {detail.seatRows.every((seat) => seat.status !== 'assigned' && seat.status !== 'member') && (
            <p style={{ fontSize: 13.5, color: color.inkFaint }}>
              Nobody has accepted an invitation here yet — a row with nobody in it is not somebody to give a key to.
            </p>
          )}
        </div>
        {note && <p style={{ fontSize: 13, color: color.inkBody, margin: '16px 0 0' }}>{note}</p>}
        <p style={{ fontSize: 12.5, color: color.inkFaint, lineHeight: 1.6, margin: '16px 0 0' }}>
          A revocation writes the serial into the gate’s deny-list and is recorded here with a reason. It takes effect on
          the next request the app makes.
        </p>
      </Card>
    </>
  );
}

/**
 * Who administers the deployment, and then every account of either kind.
 *
 * Two tables, because an administrator is not a small account: the columns this
 * one is made of — licence, plan, access, organizations — are empty for every
 * administrator, and a row of five dashes is not a row. Administrators are
 * therefore *taken out* of the list below rather than shown twice, and the
 * figures above it count what the table under them holds.
 *
 * The filter is the Worker's rather than the browser's: this list is capped at
 * 500 rows on the server, so filtering here would search a page and call it a
 * search. It is debounced by the button rather than by a timer — a keystroke is
 * not a query.
 */
export function AdminAccounts({ onInspect }: { onInspect: (id: string) => void }): React.JSX.Element {
  const [accounts, setAccounts] = useState<AdminAccount[] | null>(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'all' | 'personal' | 'org'>('all');
  const [searched, setSearched] = useState('');
  const columns = '1.5fr 100px 130px 1fr 110px 80px';

  const load = (search: string): void => {
    setSearched(search.trim());
    void api
      .get<{ accounts: AdminAccount[] }>(search ? apiPath`/admin/accounts?${apiQuery({ q: search })}` : '/admin/accounts')
      .then((answer) => setAccounts(answer.accounts))
      .catch(() => setAccounts([]));
  };

  useEffect(() => load(''), []);

  if (accounts === null) return <Empty title="Reading the accounts…" />;

  // The administrators are the table above; counting them here as well would
  // make the strip disagree with the rows it sits on top of.
  const served = accounts.filter((account) => account.role !== 'system_admin');
  const visible = kind === 'all' ? served : served.filter((account) => account.accountType === kind);
  // The strip follows the search and the segmented control, as the rows do
  // (redesign §47.4, the owner's decision in lot 12).
  const personal = visible.filter((account) => account.accountType === 'personal').length;
  const filtered = searched !== '' || kind !== 'all';
  const matches = `${visible.length} account${visible.length === 1 ? ' matches' : 's match'}`;
  const clear = (): void => {
    setQuery('');
    setSearched('');
    setKind('all');
    load('');
  };

  return (
    <>
      <Administrators />
      <div style={{ height: 30 }} />
      <div style={{ fontSize: 15, fontWeight: 600 }}>Accounts</div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 16px', lineHeight: 1.6, maxWidth: 720 }}>
        Everybody this deployment serves — personal accounts and organization members alike.
      </p>
      {visible.length === 0 ? (
        <StatStrip items={[{ label: 'Accounts', value: '0 accounts match' }]} valueSize={22} />
      ) : (
        <StatStrip
          items={[
            { label: 'Accounts', value: String(visible.length) },
            { label: 'Personal', value: String(personal) },
            { label: 'In an organization', value: String(visible.length - personal) },
            { label: 'With a licence', value: String(visible.filter((account) => account.serial !== null).length) }
          ]}
        />
      )}
      <div style={{ height: 20 }} />
      <Table
        columns={columns}
        header={['Account', 'Kind', 'Licence', 'Organizations', 'Last seen', '']}
        toolbar={
          <>
            <label htmlFor="account-filter" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
              Search accounts
            </label>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                load(query);
              }}
              style={{ display: 'flex', gap: 8 }}
            >
              <Input
                id="account-filter"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by address or name…"
                style={{ width: 'auto', minWidth: 260, fontSize: 13.5, padding: '9px 12px', borderRadius: radius.md }}
              />
              <GhostButton size="sm" onClick={() => load(query)}>
                Search
              </GhostButton>
            </form>
            {filtered && visible.length > 0 && (
              <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>{matches}</span>
            )}
            <span style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              {(['all', 'personal', 'org'] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setKind(option)}
                  aria-pressed={kind === option}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 12.5,
                    padding: '7px 12px',
                    borderRadius: radius.md,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    color: kind === option ? color.ink : color.inkSubtle,
                    background: kind === option ? color.surfaceAlt : 'transparent',
                    border: `1px solid ${kind === option ? color.lineStrong : 'transparent'}`
                  }}
                >
                  {option === 'org' ? 'Organization' : option}
                </button>
              ))}
            </div>
          </>
        }
      >
        {visible.map((account) => (
          <Row key={account.id} columns={columns}>
            <div>
              <div style={{ fontWeight: 500 }}>{account.email}</div>
              <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                {account.name ?? 'no name'}
                {account.githubLogin ? ` · gh/${account.githubLogin}` : ''}
              </div>
            </div>
            {/* The kind is a neutral mono label, not a status (redesign, lot 11). */}
            <span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 20,
                  padding: '0 6px',
                  border: `1px solid ${color.line}`,
                  borderRadius: 6,
                  fontFamily: font.mono,
                  fontSize: 10.5,
                  letterSpacing: '.12em',
                  color: color.inkMuted
                }}
              >
                {account.accountType === 'org' ? 'ORG' : 'PERSONAL'}
              </span>
            </span>
            <div>
              <StatusCell tone={ACCESS_TONE[account.access] ?? 'off'} word={ACCESS_LABEL[account.access] ?? account.access} />
              <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                {account.serials.length > 0 ? account.serials.map((serial) => `#${serial}`).join(' · ') : '—'}
              </div>
            </div>
            <span style={{ fontSize: 13, color: color.inkSubtle }}>
              {account.organizations.length === 0
                ? '—'
                : account.organizations.map((org) => `${org.name} (${org.role})`).join(', ')}
            </span>
            <DateCell at={account.lastSeenAt} empty="never" style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkFaint }} />
            <RowAction onClick={() => onInspect(account.id)}>Inspect</RowAction>
          </Row>
        ))}
      </Table>
      {visible.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <span style={{ fontSize: 13.5, color: color.inkFaint }}>0 accounts match</span>
          {filtered && (
            <GhostButton size="sm" onClick={clear}>
              Clear search
            </GhostButton>
          )}
        </div>
      )}
    </>
  );
}

/** One account: what it holds, what it paid, and where it has been. */
export function AdminAccountDetail({ id, onBack }: { id: string; onBack: () => void }): React.JSX.Element {
  const [detail, setDetail] = useState<AccountDetail | null>(null);

  useEffect(() => {
    void api
      .get<AccountDetail>(apiPath`/admin/accounts/${id}`)
      .then(setDetail)
      .catch(() => setDetail(null));
  }, [id]);
  // Grant 7 days and Revoke on the account's serial lines (§47.5).
  const serials = useSerialActions(async () => setDetail(await api.get<AccountDetail>(apiPath`/admin/accounts/${id}`)));

  if (!detail) return <Empty title="Reading this account…" />;
  // The serials no seat accounts for: a personal licence, on its own line.
  const own = detail.serials.filter((serial) => !detail.seats.some((seat) => seat.serial === serial));

  return (
    <>
      <StatStrip
        valueSize={25}
        items={[
          { label: 'Kind', value: detail.accountType === 'org' ? 'Organization' : 'Personal' },
          { label: 'Licence', value: ACCESS_LABEL[detail.access] ?? detail.access },
          { label: 'Serial', value: detail.serial ? `#${detail.serial}` : '—' },
          { label: 'Member since', value: isoDay(detail.createdAt) }
        ]}
      />

      <Card padding={24} style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{detail.email}</div>
          <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint }}>
            {detail.name ?? 'no name'}
            {detail.githubLogin ? ` · gh/${detail.githubLogin}` : ''}
            {detail.plan ? ` · ${detail.plan}` : ''}
            {detail.role === 'system_admin' ? ' · system administrator' : ''}
          </span>
          <span style={{ flex: 1 }} />
          <GhostButton size="sm" onClick={onBack}>
            ← All accounts
          </GhostButton>
        </div>
        <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '12px 0 0', lineHeight: 1.6 }}>
          {detail.organizations.length === 0
            ? detail.accountType === 'org'
              ? 'An organization account with no seat yet.'
              : 'A personal account: one licence, no organization.'
            : `Seats: ${detail.organizations
                .map((org) => `${org.name} — ${org.role}, ${org.seatStatus}`)
                .join(' · ')}`}
          {detail.lastSeenAt ? ` · last seen ${isoDay(detail.lastSeenAt)}` : ' · never signed in'}
        </p>
      </Card>
      {serials.dialog}

      {own.length > 0 && (
        <Card padding={24} style={{ marginTop: 20, maxWidth: 640 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Serials</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {own.map((serial) => (
              <SerialLine
                key={serial}
                serial={serial}
                actions={serials}
                who={
                  <>
                    {detail.email}
                    <span style={{ color: color.inkFaint }}> · {detail.plan ?? 'personal'}</span>
                  </>
                }
              />
            ))}
          </div>
        </Card>
      )}

      {/*
        Which beta this account can be given is a question about what it holds,
        not about which page somebody arrived on. A seat's window belongs on the
        seat — so an account with one is offered the very grant the
        organization's page offers, here, instead of being sent to go and find
        the organization. Everybody else is offered a personal licence.
      */}
      {/* The licence follows the account's kind: an organization account is
          never offered a personal licence, seat or none (owner, 2026-10-18). */}
      {detail.role !== 'system_admin' && detail.accountType === 'org' && detail.seats.length === 0 ? (
        <Card padding={24} style={{ marginTop: 20, maxWidth: 640 }}>
          {/* Where it waits (117a): the organization it asked to join, and since when. */}
          {detail.joinRequests.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {detail.joinRequests.map((request) => (
                <SerialLine
                  key={request.orgId}
                  serial={null}
                  actions={serials}
                  who={
                    <>
                      {detail.email}
                      <span style={{ color: color.inkFaint }}> · no seat in {request.orgName}</span>
                    </>
                  }
                  detail={
                    <span style={{ display: 'block', fontSize: 13, marginTop: 4, color: color.inkSubtle }}>
                      asked to join on {proseDay(request.requestedAt)}
                    </span>
                  }
                />
              ))}
            </div>
          )}
          <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: 0, lineHeight: 1.6 }}>
            {`This account belongs to ${
              detail.organizations[0]?.name ?? detail.joinRequests[0]?.orgName ?? 'an organization'
            }: its licence is a seat, given on the organization’s page.`}
          </p>
        </Card>
      ) : detail.role !== 'system_admin' && detail.seats.length > 0 ? (
        <BetaSeats
          userId={detail.id}
          subject={detail.email}
          seats={detail.seats}
          serials={serials}
          onDone={async () => setDetail(await api.get<AccountDetail>(apiPath`/admin/accounts/${id}`))}
        />
      ) : (
        <BetaGrant
          kind="account"
          id={detail.id}
          subject={detail.email}
          refuse={
            detail.role === 'system_admin'
              ? 'This account administers the deployment, and an administrator holds no licence: there is no Licence page on it to read one from.'
              : detail.plan && detail.plan !== 'beta'
                ? 'This account bought a licence. Moving its date forward here would be undone by the next invoice — use Grant 7 days on its serial.'
                : undefined
          }
          onDone={async () => setDetail(await api.get<AccountDetail>(apiPath`/admin/accounts/${id}`))}
        />
      )}

      <div className="al-two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 20, marginTop: 20 }}>
        <Card padding={24}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Invoices</div>
          {detail.invoices.length === 0 ? (
            <p style={{ fontSize: 13.5, color: color.inkFaint, margin: 0 }}>Nothing charged on this account.</p>
          ) : (
            detail.invoices.map((invoice) => (
              <div
                key={invoice.id}
                style={{ display: 'flex', gap: 12, fontSize: 13.5, padding: '7px 0', borderTop: `1px solid ${color.line}` }}
              >
                <DateCell at={invoice.at} style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkFaint }} />
                <span style={{ flex: 1, minWidth: 0 }}>{invoice.number ?? invoice.id}</span>
                <span>{money(invoice.amountCents, invoice.currency)}</span>
                <StatusCell tone={invoice.status === 'paid' ? 'ok' : 'warn'} word={invoice.status} />
              </div>
            ))
          )}
        </Card>

        <Card padding={24}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Trail</div>
          {detail.events.length === 0 ? (
            <p style={{ fontSize: 13.5, color: color.inkFaint, margin: 0 }}>Nothing recorded for this address.</p>
          ) : (
            detail.events.map((event, index) => (
              <div
                key={`${event.at}-${index}`}
                style={{ display: 'flex', gap: 12, fontSize: 13.5, padding: '7px 0', borderTop: `1px solid ${color.line}` }}
              >
                <DateCell at={event.at} style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkFaint }} />
                <span style={{ flex: 1, minWidth: 0 }}>{event.text}</span>
              </div>
            ))
          )}
        </Card>
      </div>
    </>
  );
}
